import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserCircleIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { guardianApi } from '../../services/api/guardian';
import { useGuardianAuthStore } from '../../stores/guardianAuthStore';
import { Card, H1, H2, Text, Badge, Button, Spinner, Alert } from '../../components/ui';
import { Input } from '../../components/forms';
import { useToast } from '../../components/ui';

const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export const GuardianProfile: React.FC = () => {
  const { guardian } = useGuardianAuthStore();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);

  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['guardian-profile'],
    queryFn: guardianApi.portal.getProfile,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    values: {
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      phone: profile?.phone || '',
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProfileFormData) => guardianApi.portal.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-profile'] });
      setIsEditing(false);
      addToast({ title: 'Success', message: 'Profile updated successfully', type: 'success' });
    },
    onError: () => {
      addToast({ title: 'Error', message: 'Failed to update profile', type: 'error' });
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    updateMutation.mutate(data);
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load profile. Please try again later.
      </Alert>
    );
  }

  const displayProfile = profile || guardian;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <H1>My Profile</H1>
          <Text className="mt-2 text-gray-600 dark:text-gray-400">
            Manage your profile information
          </Text>
        </div>
        {!isEditing && (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <PencilIcon className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2">
          <H2 className="mb-4">Personal Information</H2>
          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                {...register('first_name')}
                label="First Name"
                error={errors.first_name?.message}
              />
              <Input
                {...register('last_name')}
                label="Last Name"
                error={errors.last_name?.message}
              />
              <Input
                {...register('phone')}
                label="Phone Number"
                error={errors.phone?.message}
              />
              <div className="flex space-x-3 pt-2">
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={updateMutation.isPending}
                >
                  Save Changes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={updateMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Full Name</Text>
                <Text className="font-medium">
                  {displayProfile?.first_name} {displayProfile?.last_name}
                </Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Email</Text>
                <Text className="font-medium">{displayProfile?.email}</Text>
              </div>
              <div>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Phone</Text>
                <Text className="font-medium">
                  {displayProfile?.phone || 'Not provided'}
                </Text>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6">
          <H2 className="mb-4">Account</H2>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <UserCircleIcon className="w-10 h-10 text-primary-600 dark:text-primary-400" />
              <div>
                <Text className="font-medium">
                  {displayProfile?.first_name} {displayProfile?.last_name}
                </Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">Guardian</Text>
              </div>
            </div>
            <div>
              <Text className="text-sm text-gray-500 dark:text-gray-400">Status</Text>
              <Badge variant="success">Verified</Badge>
            </div>
          </div>
        </Card>
      </div>

      {profile?.children && profile.children.length > 0 && (
        <Card className="p-6">
          <H2 className="mb-4">Linked Children</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profile.children.map((child: any) => {
              const age = child.age != null
                ? child.age
                : child.date_of_birth
                  ? Math.floor((Date.now() - new Date(child.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
                  : null;
              return (
                <div key={child.id} className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <Text className="font-medium">
                    {child.first_name} {child.last_name}
                  </Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    {age != null ? `Age: ${age} years` : 'Age: Not available'}
                  </Text>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};
